<script lang="ts" setup>
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { emitter } from '@/main'
import { Preferences } from '@capacitor/preferences'
import { useRouter } from 'vue-router'

const userStore = useUserStore()
const authStore = useAuthStore()
const router = useRouter()

const retrying = ref(false)

async function handleRetry() {
  retrying.value = true
  try {
    await userStore.renewData()
  } finally {
    retrying.value = false
  }
}

async function handleLogout() {
  authStore.logout(emitter)
  // DEVAMS-2656: reset directly here too (defense-in-depth, independent of the
  // App.vue 'logout' emitter chain) so the overlay never survives a Log out click.
  userStore.accountLinkError = false
  await router.push({ name: 'login-azure' })
  await Preferences.remove({ key: 'userAbilities' })
}
</script>

<template>
  <div
    v-if="userStore.getAccountLinkError"
    class="account-not-linked-overlay"
  >
    <div class="overlay-content">
      <VIcon
        icon="mdi-account-alert"
        size="64"
        color="warning"
        class="mb-4"
      />
      <h2 class="text-h5 mb-3">
        Your account isn't linked yet
      </h2>
      <p class="text-body-1 mb-6">
        You've signed in with Azure AD, but there's no active PCP account linked to your identity.
        This usually means IT hasn't finished setting up your account, or your AMOS record is inactive.
        Contact IT support to get this resolved.
      </p>
      <div class="d-flex justify-center ga-3">
        <VBtn
          variant="outlined"
          color="default"
          :loading="retrying"
          @click="handleRetry"
        >
          Retry
        </VBtn>
        <VBtn
          color="primary"
          variant="elevated"
          @click="handleLogout"
        >
          Log out
        </VBtn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.account-not-linked-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2147483647;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
}

.overlay-content {
  max-width: 420px;
  padding: 2rem;
  border-radius: 0.5rem;
  background: rgb(var(--v-theme-surface));
  text-align: center;
  box-shadow: 0 0 1rem rgba(0, 0, 0, 0.4);
}
</style>
